import React from 'react';
import {
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  useInput,
  required,
  AutocompleteInput,
  FormDataConsumer,
} from 'react-admin';
import { Typography, Box, useTheme, FormHelperText } from '@mui/material';
import axios from '../axiosInstance';
import { useWatch } from 'react-hook-form';

const ColorPickerInput = ({ source, label }) => {
  const {
    field: { value, onChange },
    fieldState: { error, isTouched },
    isRequired,
  } = useInput({ source });

  const colorValue =
    typeof value === 'string' && value.startsWith('#') ? value : '#000000';

  return (
    <Box
      sx={{
        mb: 2,
        width: '100%',
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {label} {isRequired && '*'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}
      >
        <input
          id={source}
          type="color"
          value={colorValue}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 48,
            height: 48,
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            backgroundColor: 'transparent',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {colorValue}
        </Typography>
      </Box>

      {isTouched && error && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error.message}
        </FormHelperText>
      )}
    </Box>
  );
};

const ImageSelectorWithPreview = ({ source, label, choices, getFileUrl }) => {
  const selectedValue = useWatch({ name: source });

  return (
    <Box mb={2} sx={{ width: '100%' }}>
      <AutocompleteInput
        source={source}
        label={label}
        choices={choices}
        fullWidth
        optionText={(choice) =>
          choice?.name ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src={getFileUrl(choice.name)}
                alt={choice.name}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 4,
                }}
                onError={(e) => {
                  e.currentTarget.src = '';
                }}
              />
              <span>{choice.name}</span>
            </Box>
          ) : (
            ''
          )
        }
        inputText={(choice) => choice?.name || ''}
        getOptionLabel={(option) => option?.name || ''}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <img
              src={getFileUrl(option.name)}
              alt={option.name}
              style={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: 4,
              }}
              onError={(e) => {
                e.currentTarget.src = '';
              }}
            />
            {option.name}
          </Box>
        )}
        validate={required()}
      />

      {selectedValue && (
        <Box mt={1}>
          <img
            src={getFileUrl(selectedValue)}
            alt={selectedValue}
            style={{
              width: 'auto',
              height: 80,
              borderRadius: 4,
              border: '1px solid #ccc',
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const ConfigFormFields = ({ fileOptions, companyID }) => {
  const theme = useTheme();
  const bgColor = theme.palette.background.paper;

  const getFileUrl = (filename) => {
    if (!companyID || !filename) return '';
    return `${axios.defaults.baseURL}/images/${companyID}/${filename}`;
  };

  const imageChoices =
    fileOptions?.Images?.map((name) => ({ id: name, name })) || [];

  return (
    <>
      {/* Basic fields */}
      <TextInput source="name" label="Nome" validate={required()} fullWidth />

      {/* Color fields */}
      <ColorPickerInput
        source="primaryColor"
        label="Cor Primária"
        validate={required()}
      />
      <ColorPickerInput
        source="secondaryColor"
        label="Cor Secundária"
        validate={required()}
      />
      <ColorPickerInput
        source="checkMarkColor"
        label="Cor do Visto"
        validate={required()}
      />
      <ColorPickerInput
        source="primaryTextColor"
        label="Cor do Texto Primário"
        validate={required()}
      />
      <ColorPickerInput
        source="secondaryTextColor"
        label="Cor do Texto Secundário"
        validate={required()}
      />

      {/* Main Icon */}
      <ImageSelectorWithPreview
        source="mainIcon"
        label="Ícone Principal"
        choices={imageChoices}
        getFileUrl={getFileUrl}
      />

      {/* Footer */}
      <ImageSelectorWithPreview
        source="footer"
        label="Rodapé"
        choices={imageChoices}
        getFileUrl={getFileUrl}
      />

      {/* Product Icon */}
      <TextInput
        source="productIcon"
        label="Ícone do Produto"
        validate={required()}
        fullWidth
      />

      {/* Background Images */}
      <Typography variant="h6" gutterBottom>
        Imagens de Fundo
      </Typography>
      <ArrayInput source="backgroundImages" label={false}>
        <SimpleFormIterator>
          <FormDataConsumer>
            {({ scopedFormData }) => {
              const selectedImage = scopedFormData?.Name;
              return (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    padding: 2,
                    marginBottom: 2,
                    backgroundColor: bgColor,
                  }}
                >
                  <AutocompleteInput
                    source="Name"
                    label="Nome da Imagem"
                    choices={imageChoices}
                    validate={required()}
                    fullWidth
                    optionText={(choice) =>
                      choice?.name ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <img
                            src={getFileUrl(choice.name)}
                            alt={choice.name}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                            onError={(e) => {
                              e.currentTarget.src = '';
                            }}
                          />
                          <span>{choice.name}</span>
                        </Box>
                      ) : (
                        ''
                      )
                    }
                    inputText={(choice) => choice?.name || ''}
                    getOptionLabel={(option) => option?.name || ''}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <img
                          src={getFileUrl(option.name)}
                          alt={option.name}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                          onError={(e) => {
                            e.currentTarget.src = '';
                          }}
                        />
                        {option.name}
                      </Box>
                    )}
                  />

                  {selectedImage && (
                    <Box mt={1}>
                      <img
                        src={getFileUrl(selectedImage)}
                        alt={selectedImage}
                        style={{
                          width: 'auto',
                          height: 80,
                          borderRadius: 4,
                          border: '1px solid #ccc',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

export default ConfigFormFields;
